// client/src/components/pages/Settings/LexiStarWebhooks/SubscriptionDialog.tsx
//
// Create / edit a webhook subscription (ST-00207): name, HTTPS URL, event types (the
// catalogue picker). Creating returns the signing secret, which the page shows ONCE in
// SecretDialog. The server checks the URL again (public host, every address it resolves to)
// and its message is shown under the field.

import { useEffect, useRef, useState, type FormEvent } from 'react';
import { FaTimes } from 'react-icons/fa';
import type {
  WebhookSubscription,
  WebhookSubscriptionWithSecret,
} from '../../../../types/lexistar-webhooks';
import EventTypePicker from './EventTypePicker';
import { createSubscription, errorText, updateSubscription } from './webhooksApi';
import { describePatterns, normalizePatterns, urlProblem } from './webhookLogic';

export interface SubscriptionDialogProps {
  /** null = create. */
  subscription: WebhookSubscription | null;
  onClose: () => void;
  onCreated: (result: WebhookSubscriptionWithSecret) => void;
  onUpdated: (subscription: WebhookSubscription) => void;
}

const inputClass =
  'w-full px-3 py-2 text-sm border border-slate-200 rounded-lg bg-surface focus:outline-none focus:ring-2 focus:ring-primary-500';

export default function SubscriptionDialog({
  subscription,
  onClose,
  onCreated,
  onUpdated,
}: SubscriptionDialogProps) {
  const editing = subscription !== null;
  const [name, setName] = useState(subscription?.name ?? '');
  const [url, setUrl] = useState(subscription?.url ?? '');
  const [types, setTypes] = useState<string[]>(subscription?.event_types ?? ['TRIP.*']);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [urlError, setUrlError] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);
  const nameRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    nameRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const patterns = normalizePatterns(types);
  const problems = {
    name: name.trim() ? null : 'Give the webhook a name',
    url: urlProblem(url),
    types: patterns.length ? null : 'Choose at least one event type',
  };
  const valid = !problems.name && !problems.url && !problems.types;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!valid) return;
    setBusy(true);
    setError(null);
    setUrlError(null);
    try {
      if (editing && subscription) {
        const patch: { name?: string; url?: string; event_types?: string[] } = {};
        if (name.trim() !== subscription.name) patch.name = name.trim();
        if (url.trim() !== subscription.url) patch.url = url.trim();
        if (
          JSON.stringify(patterns) !== JSON.stringify(normalizePatterns(subscription.event_types))
        )
          patch.event_types = patterns;
        if (Object.keys(patch).length === 0) {
          onClose();
          return;
        }
        onUpdated(await updateSubscription(subscription.id, patch));
      } else {
        onCreated(
          await createSubscription({ name: name.trim(), url: url.trim(), event_types: patterns })
        );
      }
    } catch (err) {
      const field = (err as { response?: { data?: { field?: string } } })?.response?.data?.field;
      const message = errorText(
        err,
        editing ? 'The webhook could not be saved.' : 'The webhook could not be created.'
      );
      if (field === 'url') setUrlError(message);
      else setError(message);
    } finally {
      setBusy(false);
    }
  };

  const show = (p: string | null) => (touched ? p : null);
  const urlMessage = urlError || show(problems.url);

  return (
    <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center bg-black/60 backdrop-blur-sm overflow-y-auto py-6">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="webhook-dialog-title"
        className="bg-surface rounded-2xl shadow-2xl w-full max-w-2xl mx-4 overflow-hidden"
      >
        <form onSubmit={submit} noValidate>
          <div className="flex items-start justify-between gap-4 px-6 pt-5">
            <div>
              <h2 id="webhook-dialog-title" className="text-lg font-bold text-slate-900">
                {editing ? 'Edit webhook' : 'New webhook'}
              </h2>
              <p className="text-sm text-slate-500 mt-0.5">
                {editing
                  ? 'Changes apply to the next events. The signing secret stays the same.'
                  : 'LexiStar will POST each matching event to your endpoint, signed with a secret you get next.'}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="p-1 text-slate-400 hover:text-slate-600"
            >
              <FaTimes />
            </button>
          </div>

          <div className="px-6 py-4 space-y-4">
            <div>
              <label
                htmlFor="webhook-name"
                className="block text-sm font-medium text-slate-700 mb-1"
              >
                Name
              </label>
              <input
                id="webhook-name"
                ref={nameRef}
                value={name}
                maxLength={200}
                onChange={(e) => setName(e.target.value)}
                placeholder="ERP – trip events"
                className={inputClass}
                aria-invalid={show(problems.name) ? true : undefined}
                aria-describedby={show(problems.name) ? 'webhook-name-error' : undefined}
              />
              {show(problems.name) && (
                <p id="webhook-name-error" className="mt-1 text-xs text-red-600">
                  {problems.name}
                </p>
              )}
            </div>
            <div>
              <label
                htmlFor="webhook-url"
                className="block text-sm font-medium text-slate-700 mb-1"
              >
                Endpoint URL
              </label>
              <input
                id="webhook-url"
                value={url}
                maxLength={2048}
                inputMode="url"
                autoComplete="off"
                spellCheck={false}
                onChange={(e) => {
                  setUrl(e.target.value);
                  setUrlError(null);
                }}
                placeholder="https://erp.example.com/hooks/lexistar"
                className={`${inputClass} font-mono`}
                aria-invalid={urlMessage ? true : undefined}
                aria-describedby="webhook-url-help"
              />
              <p
                id="webhook-url-help"
                className={`mt-1 text-xs ${urlMessage ? 'text-red-600' : 'text-slate-500'}`}
              >
                {urlMessage ||
                  'https:// only, on a public host. Your endpoint must answer with a 2xx within 10 seconds.'}
              </p>
            </div>
            <div>
              <p className="block text-sm font-medium text-slate-700 mb-1">Events</p>
              <EventTypePicker value={types} onChange={setTypes} disabled={busy} />
              <p
                className={`mt-2 text-xs ${show(problems.types) ? 'text-red-600' : 'text-slate-500'}`}
              >
                {show(problems.types) || `Receives: ${describePatterns(patterns, 4)}.`}
              </p>
            </div>
            {error && (
              <p
                role="alert"
                className="px-3 py-2 text-sm rounded-lg bg-red-50 border border-red-200 text-red-700"
              >
                {error}
              </p>
            )}
          </div>

          <div className="flex justify-end gap-3 px-6 py-4 bg-slate-50 border-t border-slate-100">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-slate-600 bg-surface border border-slate-200 rounded-lg hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy}
              className="px-5 py-2 text-sm font-semibold text-white rounded-lg shadow-sm bg-primary-500 hover:bg-primary-600 disabled:opacity-60"
            >
              {busy ? 'Saving…' : editing ? 'Save changes' : 'Create webhook'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
